import { FormEvent, useState } from "react";
import { toast } from "sonner";
import { Bell, LogOut } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { MeApi } from "../api/services";
import { errorMessage } from "../api/client";
import { notifications } from "../platform";
import { env } from "../config/env";
import { Field, PageHeader, PasswordInput } from "../components/common";
import { InstallCard } from "../components/InstallCard";
import { phone as fmtPhone } from "../lib/format";

function ProfileForm() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState<any>({
    name: user.name, phone: user.phone || "", address: { line: "", barangay: "", city: "", province: "", postal_code: "", ...(user.address || {}) },
    emergency_contact: { name: "", phone: "", relationship: "", ...(user.emergency_contact || {}) }, medical_notes: user.medical_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const patient = user.role === "patient";
  const setA = (k: string) => (e: any) => setF({ ...f, address: { ...f.address, [k]: e.target.value } });
  const setC = (k: string) => (e: any) => setF({ ...f, emergency_contact: { ...f.emergency_contact, [k]: e.target.value } });

  const save = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true);
    const body: any = { name: f.name, phone: f.phone };
    if (patient) Object.assign(body, { address: f.address, emergency_contact: f.emergency_contact.phone ? f.emergency_contact : undefined, medical_notes: f.medical_notes });
    try { setUser(await MeApi.updateProfile(body)); toast.success("Profile saved"); }
    catch (ex) { toast.error(errorMessage(ex)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="fp-card space-y-4" data-testid="profile-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name"><input className="fp-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></Field>
        <Field label="Mobile (+63)" hint={`Saved as ${fmtPhone(user.phone)}`}><input className="fp-input" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone-input" /></Field>
      </div>
      {patient && (
        <>
          <p className="fp-label pt-2">Home address</p>
          <Field label="House / Unit, Street"><input className="fp-input" value={f.address.line} onChange={setA("line")} data-testid="profile-address-line-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Barangay"><input className="fp-input" value={f.address.barangay} onChange={setA("barangay")} data-testid="profile-barangay-input" /></Field>
            <Field label="City / Municipality"><input className="fp-input" value={f.address.city} onChange={setA("city")} data-testid="profile-city-input" /></Field>
            <Field label="Province"><input className="fp-input" value={f.address.province} onChange={setA("province")} data-testid="profile-province-input" /></Field>
            <Field label="ZIP code"><input className="fp-input" inputMode="numeric" value={f.address.postal_code} onChange={setA("postal_code")} data-testid="profile-zip-input" /></Field>
          </div>
          <p className="fp-label pt-2">Emergency contact</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Name"><input className="fp-input" value={f.emergency_contact.name} onChange={setC("name")} data-testid="profile-ec-name-input" /></Field>
            <Field label="Mobile"><input className="fp-input" type="tel" value={f.emergency_contact.phone} onChange={setC("phone")} data-testid="profile-ec-phone-input" /></Field>
            <Field label="Relationship"><input className="fp-input" value={f.emergency_contact.relationship} onChange={setC("relationship")} data-testid="profile-ec-relationship-input" /></Field>
          </div>
          <Field label="Medical notes for responders (optional)" hint="Allergies, conditions. Shared only with your assigned responder through dispatch.">
            <textarea className="fp-input h-24 py-2" value={f.medical_notes} onChange={(e) => setF({ ...f, medical_notes: e.target.value })} data-testid="profile-medical-notes-input" />
          </Field>
        </>
      )}
      <button className="fp-btn-primary" disabled={busy} data-testid="profile-save-button">Save profile</button>
    </form>
  );
}

function PasswordForm() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try { await MeApi.changePassword(cur, next); setCur(""); setNext(""); toast.success("Password changed"); }
    catch (ex) { toast.error(errorMessage(ex)); }
  };
  return (
    <form onSubmit={submit} className="fp-card grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-testid="password-form">
      <Field label="Current password"><PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} data-testid="password-current-input" /></Field>
      <Field label="New password"><PasswordInput minLength={8} value={next} onChange={(e) => setNext(e.target.value)} data-testid="password-new-input" /></Field>
      <button className="fp-btn-secondary h-12" data-testid="password-save-button">Change</button>
    </form>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const [perm, setPerm] = useState(notifications.permission());
  return (
    <div className="space-y-5">
      <PageHeader title="Profile" subtitle={`${user.email || user.username} · ${user.role}`} />
      <ProfileForm />
      <PasswordForm />
      <div className="fp-card flex flex-wrap items-center justify-between gap-3" data-testid="notification-permission-card">
        <div>
          <p className="font-semibold text-slate-900">Device notifications</p>
          <p className="text-sm text-slate-600">Status: <span data-testid="notification-permission-status">{perm}</span>. In-app alerts always work while FASTPASS is open.</p>
        </div>
        {perm === "default" && (
          <button className="fp-btn-secondary" data-testid="notification-enable-button" onClick={async () => setPerm(await notifications.requestPermission())}>
            <Bell size={16} /> Enable
          </button>
        )}
      </div>
      <InstallCard />
      <button onClick={logout} className="fp-btn-secondary w-full" data-testid="profile-logout-button"><LogOut size={16} /> Sign out</button>
      <p className="text-center text-xs text-slate-400">FASTPASS v{env.version}</p>
    </div>
  );
}
